import React from "react";
import DOMPurify from "dompurify";
import { wrapFootnoteRefs } from "../utils/footnoteRefs";
import { persianizeDigitsInHtml } from "../utils/persianDigits";

// Images inserted via RichTextEditor.js can carry an optional caption in
// their `data-caption` attribute. DOMPurify keeps data-* attributes by
// default, but a plain <img> can't render CSS generated content (it's a
// replaced element), so any captioned image is wrapped here in a small
// <span> group — image plus a subtitle line below it — after sanitizing.
// The alignment attribute moves from the <img> to the wrapper so the
// existing float/center rules apply to the pair as a unit.
function addImageCaptions(html) {
  if (!html.includes("data-caption")) return html;

  const doc = new DOMParser().parseFromString(html, "text/html");
  doc.querySelectorAll("img[data-caption]").forEach((img) => {
    const caption = img.getAttribute("data-caption");
    if (!caption) return;

    const align = img.getAttribute("data-align") || "center";
    img.removeAttribute("data-align");
    img.removeAttribute("data-caption");

    const wrapper = doc.createElement("span");
    wrapper.className = "rich-text-figure";
    wrapper.setAttribute("data-align", align);

    img.replaceWith(wrapper);
    wrapper.appendChild(img);

    const captionEl = doc.createElement("span");
    captionEl.className = "rich-text-image-caption";
    captionEl.textContent = caption;
    wrapper.appendChild(captionEl);
  });

  return doc.body.innerHTML;
}

// Renders admin-authored HTML (from RichTextEditor) on public pages.
// Sanitized on every render — defense in depth in case an admin account
// is ever compromised or a stray <script> ends up in stored content.
function RichText({ html, className }) {
  const clean = DOMPurify.sanitize(html || "");
  const withFootnoteRefs = wrapFootnoteRefs(clean);
  const withPersianDigits = persianizeDigitsInHtml(withFootnoteRefs);
  const withCaptions = addImageCaptions(withPersianDigits);
  return (
    <div
      className={"rich-text" + (className ? ` ${className}` : "")}
      dangerouslySetInnerHTML={{ __html: withCaptions }}
    />
  );
}

export default RichText;
